import { useCallback } from "react";
import { ReviewStatus } from "@models/job";
import type { UseJobResultsState } from "@hooks/useJobResults.types";

export interface ReviewActions {
  handleAccept: (rowIndex: number, text?: string) => void;
  handleReject: (rowIndex: number) => void;
  handleRecover: (rowIndex: number) => void;
  /** Accepts every given row in one batched save, each sealing its current
   *  pick exactly as a single-row accept does. */
  handleAcceptAll: (rowIndexes: number[]) => Promise<void>;
  handleRejectAll: (rowIndexes: number[]) => Promise<void>;
}

/**
 * Row-level review handlers (accept / reject / correct / recover), each
 * translating a user action into a `results` edit.
 */
export const useReviewActions = (
  results: UseJobResultsState,
): ReviewActions => {
  const handleAccept = useCallback(
    (rowIndex: number, text?: string): void => {
      // Accept SEALS the row's current pick: a previously picked alternative
      // (pending edit or already-saved chosen_rank) rides along explicitly so
      // the backend accepts THAT candidate — not the original top match. The
      // backend also preserves a stored pick when the rank is omitted, but
      // sending it closes the pick-PATCH-still-in-flight race.
      const chosenRank =
        results.edits.get(rowIndex)?.chosenRank ??
        results.rows.find((r) => r.rowIndex === rowIndex)?.chosenRank;
      results.applyEdit(rowIndex, {
        reviewStatus: ReviewStatus.Accepted,
        ...(chosenRank != null ? { chosenRank } : {}),
        ...(text !== undefined ? { text } : {}),
      });
    },
    [results],
  );

  const handleReject = useCallback(
    (rowIndex: number): void => {
      results.applyEdit(rowIndex, { reviewStatus: ReviewStatus.Rejected });
    },
    [results],
  );

  const handleRecover = useCallback(
    (rowIndex: number): void => {
      results.applyEdit(rowIndex, { reviewStatus: ReviewStatus.Pending });
    },
    [results],
  );

  const handleAcceptAll = useCallback(
    async (rowIndexes: number[]): Promise<void> => {
      await results.bulkApplyEdits(
        rowIndexes.map((rowIndex) => {
          const chosenRank =
            results.edits.get(rowIndex)?.chosenRank ??
            results.rows.find((r) => r.rowIndex === rowIndex)?.chosenRank;
          return {
            rowIndex,
            edit: {
              reviewStatus: ReviewStatus.Accepted,
              ...(chosenRank != null ? { chosenRank } : {}),
            },
          };
        }),
      );
    },
    [results],
  );

  const handleRejectAll = useCallback(
    async (rowIndexes: number[]): Promise<void> => {
      await results.bulkApplyEdits(
        rowIndexes.map((rowIndex) => ({
          rowIndex,
          edit: { reviewStatus: ReviewStatus.Rejected },
        })),
      );
    },
    [results],
  );

  return {
    handleAccept,
    handleReject,
    handleRecover,
    handleAcceptAll,
    handleRejectAll,
  };
};
